import React, { useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  Flex,
  Icon,
  Text,
  Tag,
  Input,
  Button,
  Avatar,
  Popover,
  iconWeight,
  pastelFromString,
} from '@nula/core-ui';
import { useSurfaceCalendarEvent } from '@/lib/calendar/use-surface-calendar-event';
import { linkEventToSurface, unlinkEventFromSurface } from '@/lib/calendar/surface-event-link';
import { formatEventTime } from '@/lib/calendar/format-event-time';
import { useCalendarStore, type CalendarEvent } from '@/stores/use-calendar-store';
import { useEntityAttendees } from '@/lib/recordings/use-entity-attendees';
import { attendeeInitial } from '@/lib/recordings/attendee-initials';
import { displayNameFor } from '@/lib/recordings/attendee-display';
import type { AttendeeView } from '@/lib/recordings/types/attendees';
import { useRecordingForSurface } from '@/lib/recordings/use-recording-for-surface';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { surfaceCapabilities } from '@/lib/sync/surface-capabilities';
import { formatPropertySummary, formatDuration } from '@/lib/sync/property-summary';
import { formatRelativeDate } from '@/lib/sync/format-relative-date';
import { useUIStore, type ActiveSurface } from '@/stores/use-ui-store';
import styles from './entity-properties.module.css';

export interface EntityPropertiesProps {
  entityId: string;
  /** The surface being read, or null at the document root. Decides both which
   *  rows exist and which entity's data they describe. */
  surface: ActiveSurface | null;
}

/**
 * The metadata under the title: a one-line summary at rest, a labelled table
 * when opened.
 *
 * Collapsed by default on every mount — the resting state of a document is its
 * content, not its metadata, and a remembered expansion would push the body
 * down on documents the reader never asked to see it on.
 */
export function EntityProperties({ entityId, surface }: EntityPropertiesProps) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  const capabilities = surfaceCapabilities(surface);
  const surfaceId = surface?.id ?? null;

  const { event } = useSurfaceCalendarEvent(entityId, surfaceId);
  const { attendees } = useEntityAttendees(entityId, surfaceId);
  const recording = useRecordingForSurface(entityId, surfaceId, surface?.recordingId ?? null);
  const workspaceDoc = useWorkspaceDoc(entityId);

  const summary = formatPropertySummary({
    event: capabilities.event ? event : null,
    attendeeCount: attendees.length,
    durationSeconds: recording?.durationSeconds ?? null,
    tagCount: workspaceDoc?.tags?.length ?? 0,
    collectionCount: workspaceDoc?.collectionIds?.length ?? 0,
    updatedAt: workspaceDoc?.updatedAt ?? null,
    capabilities,
  });

  return (
    <Flex vertical className={styles.root}>
      <Button
        variant="text"
        size="sm"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((open) => !open)}
        className={styles.toggle}
        data-testid="properties-toggle"
      >
        <Text size="xs" className={styles.summary} data-testid="properties-summary">
          {summary || 'Properties'}
        </Text>
        <Icon
          name="expand_more"
          size={12}
          weight={iconWeight.light}
          className={styles.chevron}
          aria-hidden="true"
        />
      </Button>

      {/* grid-template-rows 0fr→1fr animates to the content's own height, which
          `height: auto` cannot. The inner element owns the overflow so the row
          can collapse past its content. */}
      <div id={panelId} className={styles.panelWrap} data-open={expanded ? '' : undefined}>
        <div className={styles.panelInner}>
          {expanded && (
            <Flex vertical className={styles.panel} data-testid="properties-panel">
              {capabilities.event && surface && (
                <EventRow entityId={entityId} surfaceId={surface.id} event={event} />
              )}
              {capabilities.attendees && surface && (
                <AttendeesRow
                  entityId={entityId}
                  surfaceId={surface.id}
                  attendees={attendees}
                />
              )}
              {capabilities.recording && surface && <RecordingRow recording={recording} />}
              <DocumentRows entityId={entityId} />
            </Flex>
          )}
        </div>
      </div>
    </Flex>
  );
}

/** Faces before the group collapses the rest into a +N. Three keeps the row
 *  one line at the narrowest width the panel renders at. */
const MAX_FACES = 3;

/** How many events the picker shows at once; search reaches the rest. */
const MAX_EVENTS = 5;

interface EventRowProps {
  entityId: string;
  surfaceId: string;
  event: CalendarEvent | null;
}

/**
 * The meeting this surface holds, or the way to attach one. Surface-scoped:
 * a document holding three meetings has three of these, one per audio surface,
 * and linking here never touches the others.
 */
function EventRow({ entityId, surfaceId, event }: EventRowProps) {
  const canEdit = useCanEdit(entityId);
  const events = useCalendarStore((s) => s.events);
  const linkDocument = useCalendarStore((s) => s.linkDocument);
  const unlinkDocument = useCalendarStore((s) => s.unlinkDocument);
  const fetchEvents = useCalendarStore((s) => s.fetchEvents);
  const selectedEvent = useCalendarStore((s) => s.selectedEvent);
  const selectEvent = useCalendarStore((s) => s.selectEvent);
  const closePanel = useCalendarStore((s) => s.closePanel);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const hasFetched = useRef(false);

  // Populate the store once so the picker has something to offer on first
  // open; the row itself reads its event from a separate per-surface cache.
  useEffect(() => {
    if (hasFetched.current) return;
    if (events && Object.keys(events).length > 0) return;
    hasFetched.current = true;
    const now = new Date();
    fetchEvents(
      new Date(now.getFullYear(), now.getMonth(), 1),
      new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
    );
  }, [events, fetchEvents]);

  // Nearest-to-now first, past and future alike: the meeting you want to
  // attach notes to is the one that just happened or is about to.
  const now = Date.now();
  const byProximity = Object.values(events ?? {})
    .flat()
    .sort(
      (a, b) =>
        Math.abs(new Date(a.startAt).getTime() - now) -
        Math.abs(new Date(b.startAt).getTime() - now),
    );
  const trimmed = query.trim().toLowerCase();
  // The cap applies after the filter, so a match below it is still reachable.
  const matches = trimmed
    ? byProximity.filter((e) => e.title.toLowerCase().includes(trimmed))
    : byProximity;

  const handleSelect = async (picked: CalendarEvent) => {
    setPicking(false);
    setQuery('');
    await linkEventToSurface({ eventId: picked.id, entityId, surfaceId, linkDocument });
  };

  // A toggle: clicking the meeting you are already looking at puts the panel
  // away rather than doing nothing.
  const isShowingThisEvent = Boolean(event && selectedEvent?.id === event.id);
  const handleToggleDetails = () => {
    if (!event) return;
    if (isShowingThisEvent) closePanel();
    else selectEvent(event);
  };

  const picker = (
    <Flex vertical gap="xs" className={styles.picker}>
      <Input.Search
        label="Search events"
        size="sm"
        value={query}
        allowClear
        onChange={(e) => setQuery(e.target.value)}
      />
      {matches.length === 0 ? (
        <Text size="xs" type="secondary">
          {trimmed ? 'No matching events' : 'No events'}
        </Text>
      ) : (
        matches.slice(0, MAX_EVENTS).map((candidate) => (
          // raw-button-ok: Button wraps its children in one <span class=label>,
          // so the title and time would render side by side instead of stacked,
          // and its fixed size height clips the second line outright.
          <button
            key={candidate.id}
            type="button"
            className={styles.pickerOption}
            onClick={() => handleSelect(candidate)}
          >
            <Text size="xs" ellipsis className={styles.pickerTitle}>
              {candidate.title}
            </Text>
            <Text size="xs" type="secondary">
              {formatEventTime(candidate)}
            </Text>
          </button>
        ))
      )}
    </Flex>
  );

  return (
    <PropertyRow icon="event" label="Event" testId="property-event">
      {event ? (
        <>
          {/* A Tag like the others: one close affordance for "take this off
              the document" everywhere in the panel. Its label opens the
              meeting in the calendar panel — the row names the meeting, the
              panel is where its details live. */}
          <Tag
            bordered={false}
            closable={canEdit}
            onClose={
              canEdit
                ? () =>
                    unlinkEventFromSurface({
                      eventId: event.id,
                      entityId,
                      surfaceId,
                      unlinkDocument,
                    })
                : undefined
            }
            role="button"
            tabIndex={0}
            aria-label={
              isShowingThisEvent
                ? `Close event details: ${event.title}`
                : `Open event details: ${event.title}`
            }
            aria-pressed={isShowingThisEvent}
            onClick={handleToggleDetails}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleDetails();
              }
            }}
            className={styles.eventTag}
          >
            {event.title}
          </Tag>
          <Text size="sm" type="secondary" className={styles.plainValue}>
            {formatEventTime(event)}
          </Text>
        </>
      ) : canEdit ? (
        <Popover
          content={picker}
          title="Link to event"
          placement="bottom-start"
          trigger="click"
          role="dialog"
          open={picking}
          onOpenChange={setPicking}
        >
          {/* Direct child of Popover: it clones this to attach click and ref,
              so a wrapper would take those props instead of the button. */}
          <Button
            variant="text"
            size="sm"
            aria-label="Link an event"
          >
            + Link an event
          </Button>
        </Popover>
      ) : (
        <Text size="sm" type="secondary" className={styles.plainValue}>
          None
        </Text>
      )}
    </PropertyRow>
  );
}

/**
 * Who was in this meeting. The roster itself lives in the side panel, which
 * has room for enrichment — photo, title, company — that a property row does
 * not; this row counts them and opens it, scoped to the surface so the panel
 * shows this meeting's people rather than the app's active entity's.
 */
function AttendeesRow({
  entityId,
  surfaceId,
  attendees,
}: {
  entityId: string;
  surfaceId: string;
  attendees: readonly AttendeeView[];
}) {
  const setAttendeesPanelOpen = useUIStore((s) => s.setAttendeesPanelOpen);
  const count = attendees.length;
  const countLabel = count === 1 ? '1 person' : `${count} people`;

  return (
    <PropertyRow icon="group" label="Attendees" testId="property-attendees">
      {count > 0 ? (
        <Button
          variant="text"
          size="sm"
          // The faces are decorative — they cannot be read aloud — so the
          // accessible name carries the fact they convey.
          aria-label={`Open attendees, ${countLabel}`}
          onClick={() => setAttendeesPanelOpen(true, entityId, surfaceId)}
          className={styles.attendeesButton}
        >
          {/* Laid out here rather than with Avatar.Group: the group overlaps
              each circle by 60% of its width, which is right for photos — a
              partial face still reads — but covers most of a single letter.
              A quarter-width overlap still reads as a stack. */}
          <Flex align="center" className={styles.faces}>
            {attendees.slice(0, MAX_FACES).map((attendee) => (
              <Avatar
                key={attendee.email}
                size="sm"
                alt={displayNameFor(attendee)}
                // Hashed from the email, not the position: a person keeps the
                // same colour across surfaces, documents and reorderings.
                // eslint-disable-next-line nula-style/no-inline-style -- a per-attendee colour cannot be a static class
                style={{ backgroundColor: pastelFromString(attendee.email) }}
                className={styles.face}
              >
                {attendeeInitial(attendee)}
              </Avatar>
            ))}
            {count > MAX_FACES && (
              <Avatar size="sm" className={clsx(styles.face, styles.faceOverflow)}>
                +{count - MAX_FACES}
              </Avatar>
            )}
          </Flex>
        </Button>
      ) : (
        <Text size="sm" type="secondary" className={styles.plainValue}>
          None
        </Text>
      )}
    </PropertyRow>
  );
}

/**
 * The recording this surface holds. Read-only: its length and state are facts
 * about what happened, not settings.
 */
function RecordingRow({ recording }: { recording: { durationSeconds: number | null } | null }) {
  const label = !recording
    ? 'None'
    : recording.durationSeconds === null
      ? // Still capturing — its length is not a fact yet, and "0 min" would
        // read as a finished meeting nobody spoke at.
        'Recording…'
      : formatDuration(recording.durationSeconds);

  return (
    <PropertyRow icon="graphic_eq" label="Recording" testId="property-recording">
      <Text size="sm" type="secondary" className={styles.plainValue}>
        {label}
      </Text>
    </PropertyRow>
  );
}

interface PropertyRowProps {
  icon: string;
  label: string;
  testId: string;
  children: React.ReactNode;
}

function PropertyRow({ icon, label, testId, children }: PropertyRowProps) {
  return (
    <Flex align="start" gap="sm" className={styles.row} data-testid={testId}>
      <Flex align="center" gap="xs" className={styles.rowLabel}>
        <Icon name={icon} size={12} weight={iconWeight.light} aria-hidden="true" />
        <Text size="sm" className={styles.rowLabelText}>
          {label}
        </Text>
      </Flex>
      <Flex align="center" wrap gap="xs" className={styles.rowValue}>
        {children}
      </Flex>
    </Flex>
  );
}

/**
 * Tags, collections and last-edited: the document's own, shown on every
 * surface of it and written to the document whichever surface you are on. A
 * tag describes the note, not one page of it — `addTag` takes an entity id and
 * structurally cannot write to a surface.
 */
function DocumentRows({ entityId }: { entityId: string }) {
  const workspaceDoc = useWorkspaceDoc(entityId);
  const collections = useWorkspaceCollections();
  const service = useWorkspaceService();
  const canEdit = useCanEdit(entityId);

  const [addingTag, setAddingTag] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [pickingCollection, setPickingCollection] = useState(false);
  const [collectionQuery, setCollectionQuery] = useState('');

  const tags = workspaceDoc?.tags ?? [];
  const collectionIds = workspaceDoc?.collectionIds ?? [];
  const named = collections.filter((c) => collectionIds.includes(c.id));

  // Only collections the document is not already in: offering one it belongs
  // to would be a no-op the user has to work out for themselves.
  const query = collectionQuery.trim().toLowerCase();
  const available = collections
    .filter((c) => !collectionIds.includes(c.id))
    .filter((c) => (query ? c.name.toLowerCase().includes(query) : true));

  // A leading '#' is how people type a tag and not part of its name, so
  // '#planning' and 'planning' must not become two tags.
  const commitTag = () => {
    const trimmed = tagDraft.trim().replace(/^#/, '');
    if (trimmed) service.addTag(entityId, trimmed);
    setTagDraft('');
    setAddingTag(false);
  };

  return (
    <>
      <PropertyRow icon="sell" label="Tags" testId="property-tags">
        {tags.map((tag) => (
          <Tag
            key={tag}
            bordered={false}
            closable={canEdit}
            onClose={canEdit ? () => service.removeTag(entityId, tag) : undefined}
          >
            {tag}
          </Tag>
        ))}
        {canEdit &&
          (addingTag ? (
            <Input
              autoFocus
              size="sm"
              variant="plain"
              name="tag"
              aria-label="Tag name"
              placeholder="tag name…"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTag();
                if (e.key === 'Escape') {
                  setTagDraft('');
                  setAddingTag(false);
                }
              }}
              onBlur={commitTag}
              className={styles.tagInput}
            />
          ) : (
            <Button
              variant="text"
              size="sm"
              aria-label="Add tag"
              onClick={() => setAddingTag(true)}
            >
              + Add
            </Button>
          ))}
        {tags.length === 0 && !addingTag && !canEdit && (
          <Text size="sm" type="secondary" className={styles.plainValue}>
            None
          </Text>
        )}
      </PropertyRow>

      <PropertyRow icon="folder_open" label="Collections" testId="property-collections">
        {named.map((c) => (
          <Tag
            key={c.id}
            bordered={false}
            closable={canEdit}
            onClose={canEdit ? () => service.removeFromCollection(entityId, c.id) : undefined}
          >
            {c.name}
          </Tag>
        ))}
        {canEdit ? (
          <Popover
            content={
              <Flex vertical gap="xs" className={styles.picker}>
                <Input.Search
                  label="Search collections"
                  size="sm"
                  value={collectionQuery}
                  allowClear
                  onChange={(e) => setCollectionQuery(e.target.value)}
                />
                {available.length === 0 ? (
                  <Text size="xs" type="secondary">
                    {collectionQuery.trim() ? 'No matching collections' : 'No collections'}
                  </Text>
                ) : (
                  available.map((c) => (
                    <Button
                      key={c.id}
                      variant="text"
                      size="sm"
                      className={styles.pickerRow}
                      onClick={() => {
                        setPickingCollection(false);
                        setCollectionQuery('');
                        service.addToCollection(entityId, c.id);
                      }}
                    >
                      <Text size="xs" className={styles.pickerTitle}>
                        {c.name}
                      </Text>
                    </Button>
                  ))
                )}
              </Flex>
            }
            title="Add to collection"
            placement="bottom-start"
            trigger="click"
            role="dialog"
            open={pickingCollection}
            onOpenChange={setPickingCollection}
          >
            <Button
              variant="text"
              size="sm"
              aria-label="Add to collection"
            >
              + Add
            </Button>
          </Popover>
        ) : (
          named.length === 0 && (
            <Text size="sm" type="secondary" className={styles.plainValue}>
              None
            </Text>
          )
        )}
      </PropertyRow>

      <PropertyRow icon="schedule" label="Updated" testId="property-updated">
        <Text size="sm" type="secondary" className={styles.plainValue}>
          {workspaceDoc?.updatedAt ? formatRelativeDate(workspaceDoc.updatedAt) : '—'}
        </Text>
      </PropertyRow>
    </>
  );
}
